import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface Memorial {
  id: string;
  ownerId: string;
  fullName: string;
  birthDate?: string;
  deathDate?: string;
  biography?: string;
  personalityTraits?: string;
  favoriteThings?: string;
  hobbies?: string;
  education?: string;
  career?: string;
  familyInfo?: string;
  placesLived?: string;
  achievements?: string;
  quotes?: string;
  legacy?: string;
  organizations?: string;
  memorialServiceInfo?: string;
  profileImageUrl?: string;
  coverImageUrl?: string;
  privacy: 'private' | 'invite_only' | 'public';
  qrCodeUrl?: string;
  uniqueSlug: string;
  memoryCount?: number;
  createdAt: number;
  updatedAt: number;
  role?: string;
}

export interface CreateMemorialRequest {
  fullName: string;
  birthDate?: string;
  deathDate?: string;
  biography?: string;
  personalityTraits?: string;
  favoriteThings?: string;
  hobbies?: string;
  education?: string;
  career?: string;
  familyInfo?: string;
  placesLived?: string;
  achievements?: string;
  quotes?: string;
  legacy?: string;
  organizations?: string;
  memorialServiceInfo?: string;
  privacy: 'private' | 'invite_only' | 'public';
}

export interface UpdateMemorialRequest {
  fullName?: string;
  birthDate?: string;
  deathDate?: string;
  biography?: string;
  personalityTraits?: string;
  favoriteThings?: string;
  hobbies?: string;
  education?: string;
  career?: string;
  familyInfo?: string;
  placesLived?: string;
  achievements?: string;
  quotes?: string;
  legacy?: string;
  organizations?: string;
  memorialServiceInfo?: string;
  privacy?: 'private' | 'invite_only' | 'public';
  profileImageR2Key?: string;
  coverImageR2Key?: string;
}

@Injectable({
  providedIn: 'root',
})
export class MemorialService {
  constructor(private api: ApiService) {}

  list(): Observable<{ memorials: Memorial[] }> {
    return this.api.get<{ memorials: Memorial[] }>('/memorials');
  }

  listPublic(): Observable<{ memorials: Memorial[] }> {
    return this.api.get<{ memorials: Memorial[] }>('/memorials/public');
  }

  getById(id: string, token?: string): Observable<Memorial> {
    const url = token ? `/memorials/${id}?token=${encodeURIComponent(token)}` : `/memorials/${id}`;
    return this.api.get<Memorial>(url);
  }

  getBySlug(slug: string, token?: string): Observable<Memorial> {
    const url = token ? `/memorials/public/${slug}?token=${encodeURIComponent(token)}` : `/memorials/public/${slug}`;
    return this.api.get<Memorial>(url);
  }

  getTokenBySlug(slug: string): Observable<{ token: string } | { public: true }> {
    return this.api.get<{ token: string } | { public: true }>(`/memorials/public/${slug}/token`);
  }

  create(data: CreateMemorialRequest): Observable<Memorial> {
    return this.api.post<Memorial>('/memorials', data);
  }

  update(id: string, data: UpdateMemorialRequest): Observable<Memorial> {
    return this.api.patch<Memorial>(`/memorials/${id}`, data);
  }

  delete(id: string): Observable<{ success: boolean }> {
    return this.api.delete<{ success: boolean }>(`/memorials/${id}`);
  }

  getQRCode(id: string): Observable<Blob> {
    return this.api.getBlob(`/memorials/${id}/qr`);
  }

  addAccess(
    memorialId: string,
    userId: string,
    role: 'contributor' | 'viewer'
  ): Observable<{ success: boolean }> {
    return this.api.post<{ success: boolean }>(`/memorials/${memorialId}/access`, {
      userId,
      role,
    });
  }

  removeAccess(memorialId: string, userId: string): Observable<{ success: boolean }> {
    return this.api.delete<{ success: boolean }>(`/memorials/${memorialId}/access/${userId}`);
  }

  uploadProfileImage(memorialId: string, file: File): Observable<{ r2Key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.upload<{ r2Key: string }>(`/memorials/${memorialId}/profile-image`, formData);
  }

  uploadCoverImage(memorialId: string, file: File): Observable<{ r2Key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.upload<{ r2Key: string }>(`/memorials/${memorialId}/cover-image`, formData);
  }

  searchUsers(memorialId: string, email: string): Observable<{ users: UserSearchResult[] }> {
    return this.api.get<{ users: UserSearchResult[] }>(`/memorials/${memorialId}/users/search?email=${encodeURIComponent(email)}`);
  }

  getAccessList(memorialId: string): Observable<{ access: MemorialAccess[] }> {
    return this.api.get<{ access: MemorialAccess[] }>(`/memorials/${memorialId}/access`);
  }
}

export interface UserSearchResult {
  id: string;
  email: string; // Partially masked
  displayName?: string;
}

export interface MemorialAccess {
  id: string;
  memorialId: string;
  userId: string;
  userEmail: string;
  userDisplayName?: string;
  role: 'owner' | 'contributor' | 'viewer';
  createdAt: number;
}
